import dayjs from 'dayjs'
import { formatString } from '../../../utils'

export const buildPrintHTML = (order, isCactusAdmin) => {
  const statusText =
    isCactusAdmin && order.status === 'SUBMITTED_TO_CACTUS'
      ? 'Received'
      : formatString(order.status)

  return `
    <html>
      <head>
        <title>Custom Saddle Order - ${order.id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          h1 { color: #2b364b; border-bottom: 2px solid #5C80B6; padding-bottom: 10px; }
          h2 { color: #2b364b; margin-top: 30px; font-size: 18px; }
          .section { margin-bottom: 20px; }
          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .item { background: #f7f7f6; padding: 15px; border-radius: 8px; }
          .label { font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .value { font-size: 16px; font-weight: bold; color: #2b364b; }
          .measurement { background: #e8f0fc; border: 1px solid #5C80B6; }
          .notes { background: #fffbeb; padding: 20px; border-radius: 8px; border: 1px solid #fcd34d; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Custom Saddle Order Form</h1>
        <p><strong>Order ID:</strong> ${order.id}</p>
        <p><strong>Date:</strong> ${dayjs(order.createdAt).format('MMMM D, YYYY [at] h:mm A')}</p>
        <p><strong>Status:</strong> ${statusText}</p>

        <h2>Horse Information</h2>
        <div class="grid">
          <div class="item"><div class="label">Horse Name</div><div class="value">${order.horseName}</div></div>
        </div>

        <h2>Horse Measurements (Internal Use)</h2>
        <div class="grid">
          <div class="item measurement"><div class="label">Gullet Width</div><div class="value">${order.gulletWidth || 'N/A'}</div></div>
          <div class="item measurement"><div class="label">Bar Angle</div><div class="value">${order.barAngle || 'N/A'}°</div></div>
        </div>

        <h2>Saddle Specifications</h2>
        <div class="grid">
          <div class="item"><div class="label">Seat Size</div><div class="value">${order.saddleSpecs?.seatSize || 'N/A'}"</div></div>
          <div class="item"><div class="label">Tree Type</div><div class="value">${formatString(order.saddleSpecs?.treeType) || 'N/A'}</div></div>
          <div class="item"><div class="label">Discipline</div><div class="value">${formatString(order.saddleSpecs?.discipline) || 'N/A'}</div></div>
          <div class="item"><div class="label">Skirt Length</div><div class="value">${order.saddleSpecs?.skirtLength || 'N/A'}"</div></div>
          <div class="item"><div class="label">Cantle Height</div><div class="value">${order.saddleSpecs?.cantleHeight || 'N/A'}"</div></div>
          <div class="item"><div class="label">Horn Size</div><div class="value">${order.saddleSpecs?.hornSize || 'N/A'}</div></div>
          <div class="item"><div class="label">Rigging Type</div><div class="value">${formatString(order.saddleSpecs?.riggingType) || 'N/A'}</div></div>
          <div class="item"><div class="label">Ground Seat</div><div class="value">${order.saddleSpecs?.groundSeat || 'N/A'}</div></div>
          <div class="item"><div class="label">Leather Type</div><div class="value">${formatString(order.saddleSpecs?.leatherType) || 'N/A'}</div></div>
          <div class="item"><div class="label">Leather Color</div><div class="value">${order.saddleSpecs?.leatherColor || 'N/A'}</div></div>
          <div class="item"><div class="label">Saddle Weight</div><div class="value">${order.saddleSpecs?.saddleWeight || 'N/A'} lbs</div></div>
        </div>

        <h2>Shipping Address</h2>
        <div class="section">
          <p><strong>${order.shippingAddress?.fullName || 'N/A'}</strong></p>
          <p>${order.shippingAddress?.addressLine1 || ''}</p>
          ${order.shippingAddress?.addressLine2 ? `<p>${order.shippingAddress.addressLine2}</p>` : ''}
          <p>${[order.shippingAddress?.city, order.shippingAddress?.state, order.shippingAddress?.postalCode].filter(Boolean).join(', ')}</p>
          <p>${order.shippingAddress?.country || ''}</p>
          ${order.shippingAddress?.phone ? `<p>Phone: ${order.shippingAddress.phone}</p>` : ''}
        </div>

        ${order.adminNotes ? `
          <h2>Admin Notes</h2>
          <div class="notes">${order.adminNotes}</div>
        ` : ''}

        <p style="margin-top: 40px; color: #999; font-size: 12px;">
          Printed on ${dayjs().format('MMMM D, YYYY [at] h:mm A')}
        </p>
      </body>
    </html>
  `
}

/**
 * Open a new window and print the order.
 */
export const printOrder = (order, isCactusAdmin) => {
  const printWindow = window.open('', '_blank')
  printWindow.document.write(buildPrintHTML(order, isCactusAdmin))
  printWindow.document.close()
  printWindow.print()
}

/**
 * Export `orders` as a CSV file download.
 */
export const exportOrdersToCSV = (orders) => {
  if (!orders.length) return

  const headers = [
    'Order ID',
    'Horse Name',
    'Status',
    'Gullet Width',
    'Bar Angle',
    'Seat Size',
    'Tree Type',
    'Discipline',
    'City',
    'State',
    'Country',
    'Created At',
  ]

  const rows = orders.map((order) => [
    order.id,
    order.horseName,
    order.status,
    order.gulletWidth || 'N/A',
    order.barAngle || 'N/A',
    order.saddleSpecs?.seatSize || 'N/A',
    order.saddleSpecs?.treeType || 'N/A',
    order.saddleSpecs?.discipline || 'N/A',
    order.shippingAddress?.city || 'N/A',
    order.shippingAddress?.state || 'N/A',
    order.shippingAddress?.country || 'N/A',
    dayjs(order.createdAt).format('YYYY-MM-DD HH:mm'),
  ])

  const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `custom-saddle-orders-${dayjs().format('YYYY-MM-DD')}.csv`
  a.click()
  window.URL.revokeObjectURL(url)
}
